import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import type { InventoryItem, InventoryLocation } from '../../shared/api/types';
import { Alert, Button, Field } from '../../shared/ui/components';
import { parseQuantity } from './labels';

type Kind = 'entry' | 'exit' | 'consumption' | 'return' | 'adjustment' | 'transfer';
const KINDS: Record<Kind, string> = { entry: 'Entrada', exit: 'Salida', consumption: 'Consumo', return: 'Devolución', transfer: 'Traslado', adjustment: 'Ajuste' };
const DONE: Record<Kind, string> = { entry: 'Entrada registrada.', exit: 'Salida registrada.', consumption: 'Consumo registrado.', return: 'Devolución registrada.', transfer: 'Traslado registrado.', adjustment: 'Ajuste registrado.' };

function useSubmit(onDone: (message: string) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function run(action: () => Promise<unknown>, message: string, form?: HTMLFormElement) {
    setBusy(true);
    setError(null);
    try {
      await action();
      form?.reset();
      onDone(message);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, setError, run };
}

export type MovementChoice = 'entry' | 'exit' | 'more';
const MORE: Kind[] = ['consumption', 'return', 'transfer', 'adjustment'];

/** Registrar una entrada o una salida; «Más movimientos» deja elegir consumo, devolución, traslado o ajuste. */
export function MovementForm({ churchId, items, locations, choice, onDone }: { churchId: string; items: InventoryItem[]; locations: InventoryLocation[]; choice: MovementChoice; onDone: (m: string) => void }) {
  const [picked, setKind] = useState<Kind>('consumption');
  const kind: Kind = choice === 'more' ? picked : choice;
  const { busy, error, setError, run } = useSubmit(onDone);

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const quantity = parseQuantity(String(form.get('quantity') ?? ''));
    if (quantity === null || (kind !== 'adjustment' && quantity < 0)) {
      setError(kind === 'adjustment' ? 'Escribe cuánto sumar (positivo) o restar (negativo), hasta tres decimales.' : 'Escribe una cantidad mayor que cero, hasta tres decimales.');
      return;
    }
    const note = String(form.get('note') ?? '').trim() || null;
    const itemId = String(form.get('itemId'));
    const body =
      kind === 'transfer'
        ? { itemId, fromLocationId: String(form.get('locationId')), toLocationId: String(form.get('toLocationId')), quantity, note }
        : { itemId, locationId: String(form.get('locationId')), kind, quantity, note };
    void run(() => api.post(`/inventory/churches/${churchId}/${kind === 'transfer' ? 'transfers' : 'movements'}`, body), DONE[kind], formEl);
  }

  const select = (name: string, label: string, options: { id: string; name: string }[]) => (
    <div className="field">
      <label htmlFor={`inv-${name}`}>{label}</label>
      <select id={`inv-${name}`} name={name} required>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      {choice === 'more' ? (
        <div className="field">
          <label htmlFor="inv-kind">Movimiento</label>
          <select id="inv-kind" value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
            {MORE.map((k) => (
              <option key={k} value={k}>
                {KINDS[k]}
              </option>
            ))}
          </select>
        </div>
      ) : null}
      {select('itemId', 'Artículo', items.map((i) => ({ id: i.id, name: `${i.name} (${i.unit})` })))}
      {select('locationId', kind === 'transfer' ? 'Desde' : 'Lugar', locations)}
      {kind === 'transfer' ? select('toLocationId', 'Hacia', locations) : null}
      <Field label={kind === 'adjustment' ? 'Cantidad (+ suma, − resta)' : 'Cantidad'} name="quantity" inputMode="decimal" required autoComplete="off" />
      <Field label={kind === 'adjustment' ? 'Motivo del ajuste' : 'Nota (opcional)'} name="note" maxLength={300} required={kind === 'adjustment'} />
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          {choice === 'more' ? 'Registrar' : `Registrar ${KINDS[kind].toLowerCase()}`}
        </Button>
      </div>
    </form>
  );
}

/** Nuevo artículo o, con `item`, corregir uno que ya existe (la existencia no cambia). */
export function NewItemForm({ churchId, item, onDone, onCancel }: { churchId: string; item?: InventoryItem; onDone: (m: string) => void; onCancel?: () => void }) {
  const { busy, error, setError, run } = useSubmit(onDone);
  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const minText = String(form.get('minQuantity') ?? '').trim();
    const minQuantity = minText ? parseQuantity(minText) : null;
    if (minText && (minQuantity === null || minQuantity < 0)) {
      setError('El mínimo debe ser un número positivo.');
      return;
    }
    const body = {
      name: String(form.get('name')),
      sku: String(form.get('sku') ?? '').trim() || null,
      unit: String(form.get('unit')),
      kind: String(form.get('kind')),
      minQuantity,
    };
    void run(
      () => (item ? api.put(`/inventory/churches/${churchId}/items/${item.id}`, body) : api.post(`/inventory/churches/${churchId}/items`, body)),
      item ? 'Artículo actualizado.' : 'Artículo creado.',
      item ? undefined : formEl,
    );
  }
  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <Field label="Nombre" name="name" required minLength={2} maxLength={120} placeholder="Café molido" defaultValue={item?.name} />
      <Field label="Unidad" name="unit" required maxLength={20} placeholder="kg, pieza, litro, caja" defaultValue={item?.unit} />
      <div className="field">
        <label htmlFor={item ? `item-kind-${item.id}` : 'item-kind'}>Tipo</label>
        <select id={item ? `item-kind-${item.id}` : 'item-kind'} name="kind" defaultValue={item?.kind ?? 'consumable'}>
          <option value="consumable">Consumible (se gasta)</option>
          <option value="asset">Recurso o equipo (se presta y vuelve)</option>
        </select>
      </div>
      <Field label="Avisar cuando haya menos de (opcional)" name="minQuantity" inputMode="decimal" autoComplete="off" defaultValue={item?.minQuantity ?? undefined} />
      <Field label="Clave (opcional)" name="sku" maxLength={40} defaultValue={item?.sku ?? undefined} />
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          {item ? 'Guardar artículo' : 'Crear artículo'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** Nuevo lugar o, con `location`, cambiarle el nombre. */
export function NewLocationForm({ churchId, location, onDone, onCancel }: { churchId: string; location?: InventoryLocation; onDone: (m: string) => void; onCancel?: () => void }) {
  const { busy, error, run } = useSubmit(onDone);
  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const body = { name: String(new FormData(formEl).get('name')) };
    if (location) void run(() => api.put(`/inventory/churches/${churchId}/locations/${location.id}`, body), 'Lugar actualizado.');
    else void run(() => api.post(`/inventory/churches/${churchId}/locations`, body), 'Lugar creado.', formEl);
  }
  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <Field label="Nombre del lugar" name="name" required minLength={2} maxLength={80} placeholder="Bodega, Cocina, Cabina de sonido" defaultValue={location?.name} />
      {error ? <Alert>{error}</Alert> : null}
      <div className="actions">
        <Button type="submit" busy={busy}>
          {location ? 'Guardar lugar' : 'Crear lugar'}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        ) : null}
      </div>
    </form>
  );
}
